import { useMutation, useQuery } from '@apollo/client'
import { useIsAuthenticated } from '@azure/msal-react'
import CloseIcon from '@mui/icons-material/Close'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import sortBy from 'lodash/sortBy'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import CommonPageWrapper from '../../common/components/CommonPageWrapper'
import Container from '../../common/container/Container'
import Loader from '../../common/loader/Loader'
import { i18n } from '../index/index'
import { ExpressCheckoutProductAccordion } from './ExpressCheckoutProductAvailabilities/ExpressCheckoutProductAccordion'
import {
  fetchExpressCheckoutProductAvailabilities,
  upsertExpressCheckoutProductAvailabilities,
} from './expressCheckoutQueries'
import {
  ModifierGroupType,
  ProductAvailabilitiesFormValues,
  ProductAvailability,
  ProductAvailabilityCategory,
  ProductModifier,
  ProductStatus,
  ProductType,
} from './types'

const HIDDEN_MODIFIER_GROUP_TYPES: string[] = [ModifierGroupType.RECIPE]

export default function ExpressCheckoutProductAvailabilitiesPage() {
  const { stationId } = useParams<{ stationId: string }>()

  const [error, setError] = React.useState<string | null>(null)
  const [initialProductValues, setInitialProductValues] = React.useState<Record<string, boolean>>({})
  const [initialModifierValues, setInitialModifierValues] = React.useState<Record<string, boolean>>({})
  const [modifierMap, setModifierMap] = React.useState<Record<string, ProductModifier>>({})

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, dirtyFields },
  } = useForm<ProductAvailabilitiesFormValues>({
    defaultValues: { products: {}, modifiers: {}, filter: '' },
  })

  const isAuthenticated = useIsAuthenticated()
  const { data, loading } = useQuery<
    { expressCheckoutProductAvailabilities: ProductAvailabilityCategory[] },
    { stationId: string }
  >(fetchExpressCheckoutProductAvailabilities, {
    variables: { stationId: decodeURIComponent(stationId) },
    errorPolicy: 'all',
    skip: !isAuthenticated || !stationId,
    fetchPolicy: 'network-only',
    onError: () => setError(i18n.t('restaurantMenu.errorMessages.fetchProductAvailabilitiesFailed')),
  })

  const [onMutate] = useMutation(upsertExpressCheckoutProductAvailabilities, {
    refetchQueries: [
      {
        query: fetchExpressCheckoutProductAvailabilities,
        variables: { stationId: decodeURIComponent(stationId) },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      },
    ],
    onError: () => setError(i18n.t('restaurantMenu.errorMessages.upsertProductAvailabilitiesFailed')),
  })

  const handleClearFilter = () => setValue('filter', '')

  const onSubmit = async (values: ProductAvailabilitiesFormValues) => {
    const unavailableProducts = Object.entries(values.products)
      .filter(([_, v]) => v === false)
      .map(([k]) => ({ productId: k, productType: ProductType.PRODUCT }))
    const unavailableModifiers = Object.entries(values.modifiers)
      .filter(([_, v]) => v === false)
      .map(([k]) => ({
        productId: k,
        productType: ProductType.MODIFIER,
        modifierGroupType: modifierMap[k].type,
      }))
    const allUnavailable = [...unavailableProducts, ...unavailableModifiers]
    await onMutate({
      variables: {
        stationId: decodeURIComponent(stationId),
        unavailableProducts: allUnavailable,
      },
    })
  }

  const categories = data?.expressCheckoutProductAvailabilities ?? []

  useEffect(() => {
    if (categories.length > 0) {
      const products = getProductMap(categories)
      const modifiers = getModifierMap(categories)
      setModifierMap(modifiers)
      const productsObj: Record<string, boolean> = Object.values(products).reduce(
        (acc, product) => {
          acc[product.productId] = product.available
          return acc
        },
        {} as Record<string, boolean>,
      )
      const modifiersObj: Record<string, boolean> = Object.values(modifiers).reduce(
        (acc, modifier) => {
          acc[modifier.productId] = modifier.available
          return acc
        },
        {} as Record<string, boolean>,
      )
      reset({ products: productsObj, modifiers: modifiersObj, filter: '' })
      setInitialProductValues(productsObj)
      setInitialModifierValues(modifiersObj)
    }
  }, [categories, reset])

  const productValues = watch('products')
  const modifierValues = watch('modifiers')
  const filterText = watch('filter')

  const filteredProductCategories = filterProductCategories(categories, filterText)
  const filteredModifierCategories = filterModifierCategories(modifierMap, filterText)

  const isEditable = !isSubmitting && !loading
  const isDirty =
    (dirtyFields.products && Object.keys(dirtyFields.products).length > 0) ||
    (dirtyFields.modifiers && Object.keys(dirtyFields.modifiers).length > 0)

  const handleProductToggle = (productId: string, available: boolean) => {
    if (modifierValues[productId] !== undefined) {
      setValue(`modifiers.${productId}`, available)
    }
  }

  const handleModifierToggle = (modifierId: string, available: boolean) => {
    if (productValues[modifierId] !== undefined) {
      setValue(`products.${modifierId}`, available)
    }
  }

  return (
    <CommonPageWrapper title={i18n.t('restaurantMenu.productAvailabilities.title')}>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity='error'>
          {error}
        </Alert>
      </Snackbar>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <Grid container justifyContent='center' alignItems='center'>
            <Typography variant='h4' sx={{ mb: 2 }}>
              {i18n.t('restaurantMenu.productAvailabilities.title')}
            </Typography>
          </Grid>
          <Loader loading={loading}>
            {categories.length === 0 ? (
              <Grid container justifyContent='center'>
                <Typography>{i18n.t('restaurantMenu.errorMessages.dataNotFound')}</Typography>
              </Grid>
            ) : (
              <Grid>
                <Controller
                  name='filter'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={i18n.t('restaurantMenu.productAvailabilities.filter')}
                      variant='outlined'
                      value={field.value}
                      onChange={field.onChange}
                      sx={{ mb: 2 }}
                      fullWidth
                      slotProps={{
                        input: {
                          endAdornment: field.value ? (
                            <InputAdornment position='end'>
                              <IconButton onClick={handleClearFilter} size='small' edge='end' aria-label='clear'>
                                <CloseIcon />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        },
                      }}
                    />
                  )}
                />
                <Typography variant='h5' sx={{ mb: 1 }}>
                  {i18n.t('restaurantMenu.productAvailabilities.products')}
                </Typography>
                <Grid sx={{ mb: 2 }}>
                  {filteredProductCategories.length === 0 ? (
                    <Typography>{i18n.t('restaurantMenu.productAvailabilities.noProducts')}</Typography>
                  ) : (
                    filteredProductCategories.map((category, i) => (
                      <ExpressCheckoutProductAccordion
                        key={i}
                        category={category}
                        control={control}
                        productValues={productValues}
                        disabled={!isEditable}
                        productType={ProductType.PRODUCT}
                        onToggle={handleProductToggle}
                      />
                    ))
                  )}
                </Grid>
                <Typography variant='h5' sx={{ mb: 1 }}>
                  {i18n.t('restaurantMenu.productAvailabilities.components')}
                </Typography>
                <Grid>
                  {filteredModifierCategories.length === 0 ? (
                    <Typography>{i18n.t('restaurantMenu.productAvailabilities.noComponents')}</Typography>
                  ) : (
                    filteredModifierCategories.map((category, i) => (
                      <ExpressCheckoutProductAccordion
                        key={i}
                        category={category}
                        control={control}
                        productValues={modifierValues}
                        disabled={!isEditable}
                        productType={ProductType.MODIFIER}
                        onToggle={handleModifierToggle}
                      />
                    ))
                  )}
                </Grid>
              </Grid>
            )}
          </Loader>
        </Container>
        {categories.length > 0 && (
          <Grid
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1150,
              bgcolor: 'background.paper',
              boxShadow: 3,
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button variant='contained' type='submit' disabled={!isEditable || !isDirty}>
              {i18n.t('buttons.save')}
            </Button>
            <Button
              variant='outlined'
              disabled={!isEditable || !isDirty}
              onClick={() =>
                reset({
                  products: initialProductValues,
                  modifiers: initialModifierValues,
                  filter: filterText,
                })
              }
            >
              {i18n.t('buttons.cancel')}
            </Button>
          </Grid>
        )}
      </form>
    </CommonPageWrapper>
  )
}

const getProductMap = (categories: ProductAvailabilityCategory[]): Record<string, ProductAvailability> => {
  const productMap: Record<string, ProductAvailability> = {}
  const collectProducts = (category: ProductAvailabilityCategory) => {
    category.products?.forEach((product) => {
      if (product.status === ProductStatus.AVAILABLE && !productMap.hasOwnProperty(product.productId)) {
        productMap[product.productId] = product
      }
    })
    category.categories?.forEach(collectProducts)
  }
  categories.forEach(collectProducts)
  return productMap
}

const getModifierMap = (categories: ProductAvailabilityCategory[]): Record<string, ProductModifier> => {
  const modifierMap: Record<string, ProductModifier> = {}
  const collectModifiers = (category: ProductAvailabilityCategory) => {
    category.products?.forEach((product) => {
      if (product.status === ProductStatus.AVAILABLE && product.modifiers) {
        product.modifiers.forEach((modifier) => {
          if (!modifierMap.hasOwnProperty(modifier.productId)) {
            modifierMap[modifier.productId] = modifier
          }
        })
      }
    })
    category.categories?.forEach(collectModifiers)
  }
  categories.forEach(collectModifiers)
  return modifierMap
}

const filterProductCategories = (
  categories: ProductAvailabilityCategory[],
  filterText: string,
): ProductAvailabilityCategory[] => {
  const filterTextLower = filterText.toLowerCase()
  const filterCategory = (category: ProductAvailabilityCategory): ProductAvailabilityCategory | null => {
    const filteredProducts = category.products?.filter(
      (product) =>
        product.status === ProductStatus.AVAILABLE &&
        (product.name?.toLowerCase().includes(filterTextLower) ||
          product.productId.toLowerCase().includes(filterTextLower) ||
          (product.ean && product.ean.toLowerCase().includes(filterTextLower))),
    )
    const filteredSubcategories = category.categories?.map(filterCategory).filter(Boolean) as
      | ProductAvailabilityCategory[]
      | undefined
    if (
      (filteredProducts && filteredProducts.length > 0) ||
      (filteredSubcategories && filteredSubcategories.length > 0)
    ) {
      const sortedProducts = filteredProducts
        ? sortBy(filteredProducts, (product) => product.name?.toLowerCase() || '')
        : undefined
      return {
        ...category,
        products: sortedProducts,
        categories: filteredSubcategories,
      }
    }
    return null
  }
  return categories.map(filterCategory).filter(Boolean) as ProductAvailabilityCategory[]
}

const filterModifierCategories = (
  modifierMap: Record<string, ProductModifier>,
  filterText: string,
): ProductAvailabilityCategory[] => {
  const modifierGroupMap: Record<string, ProductAvailability[]> = {}
  const filterTextLower = filterText ? filterText.toLowerCase() : null
  Object.values(modifierMap).forEach((modifier) => {
    if (HIDDEN_MODIFIER_GROUP_TYPES.includes(modifier.type)) {
      return
    }
    const matches =
      !filterTextLower ||
      modifier.name?.toLowerCase().includes(filterTextLower) ||
      modifier.productId.toLowerCase().includes(filterTextLower)
    if (matches) {
      if (!modifierGroupMap[modifier.type]) {
        modifierGroupMap[modifier.type] = []
      }
      modifierGroupMap[modifier.type].push({
        productId: modifier.productId,
        name: modifier.name,
        available: modifier.available,
      })
    }
  })
  const categories = Object.entries(modifierGroupMap).map(([type, products]) => ({
    name: getModifierGroupTypeTranslation(type),
    products: sortBy(products, (product) => product.name?.toLowerCase() || ''),
  }))
  const sortedCategories = sortBy(categories, (category) => category.name?.toLowerCase() || '')
  return sortedCategories
}

const getModifierGroupTypeTranslation = (type: string): string => {
  const keyMap = {
    [ModifierGroupType.DRINK]: 'drink',
    [ModifierGroupType.RECIPE]: 'recipe',
    [ModifierGroupType.OTHER]: 'other',
    [ModifierGroupType.SALAD_BUFFET]: 'saladBuffet',
    [ModifierGroupType.SAUCE]: 'sauce',
    [ModifierGroupType.SIDE_DISH]: 'sidedish',
    [ModifierGroupType.PIZZA]: 'pizza',
    [ModifierGroupType.CHOICE_GENERIC]: 'choiceGeneric',
    [ModifierGroupType.OPTION]: 'option',
    [ModifierGroupType.EXTRA]: 'extra',
  }
  const key = keyMap[type]
  return key ? i18n.t(`restaurantMenu.productAvailabilities.${key}`) : type
}
